// API client de ingesta de hojas de cálculo.
// Backend: mcp_django/apps/ingest/ — sube el archivo, lo materializa en
// staging y devuelve su PERFIL (schema + muestra), nunca las filas.
//
// 🔒 SINGLE-SOURCE · el único lugar del dashboard que sube tabulares a
// /admin/ingest. El resto de adjuntos del chat van al Drive (documents/api).

import { ensureFreshToken } from '@/lib/tokenRefresh';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/ingest';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}` };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body}`);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ── Tipos (espejo de apps/ingest/schemas.py) ──────────

export interface IngestColumn {
  key: string;
  label: string;
  type: string;
  null_pct: number;
  samples: string[];
}

export interface IngestProfile {
  sheet: string;
  sheets_available: string[];
  header_row: number;
  row_count: number;
  empty_rows_skipped: number;
  columns: IngestColumn[];
  preview: Array<Record<string, unknown>>;
  note: string;
}

export interface IngestDataset {
  id: string;
  filename: string;
  sheet: string;
  sheet_names: string[];
  status: string;
  header_row: number;
  row_count: number;
  error: string;
  created_at: string | null;
}

export interface IngestUploadOut {
  dataset: IngestDataset;
  profile: IngestProfile;
}

// ── Detección de tabulares ────────────────────────────

/**
 * Extensiones que van a ingesta en vez de al Drive.
 *
 * Se decide por EXTENSIÓN y no por `file.type`: los .xlsx exportados por
 * sistemas viejos (como el ERP de rentalmaq) llegan con MIME vacío o
 * `application/octet-stream`, y por MIME se irían al Drive sin que nadie
 * lo note.
 */
const TABULAR_EXT = ['.xlsx', '.xls', '.xlsm', '.csv', '.tsv'];

export function isTabularFile(file: File): boolean {
  const name = (file.name || '').toLowerCase();
  return TABULAR_EXT.some((ext) => name.endsWith(ext));
}

// ── Operaciones ───────────────────────────────────────

export async function uploadDataset(
  file: File,
  sheet = '',
): Promise<IngestUploadOut> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('sheet', sheet);
  const res = await fetch(`${DJANGO_PROXY}${BASE}/upload`, {
    method: 'POST',
    headers: await authHeaders(), // sin Content-Type: el browser pone el boundary
    body: fd,
  });
  return asJson<IngestUploadOut>(res);
}

export async function listDatasets(limit = 50): Promise<IngestDataset[]> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/datasets?limit=${limit}`, {
    headers: await authHeaders(),
  });
  const data = await asJson<{ items: IngestDataset[] } | IngestDataset[]>(res);
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function getDataset(datasetId: string): Promise<IngestUploadOut> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/datasets/${datasetId}`, {
    headers: await authHeaders(),
  });
  return asJson<IngestUploadOut>(res);
}

export async function deleteDataset(datasetId: string): Promise<void> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/datasets/${datasetId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await asJson<null>(res);
}
